import React from 'react';
import { AppProvider, useApp } from './context/AppContext';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Toast from './components/Toast';
import DetailPanel from './modals/DetailPanel';

// Pages
import RentalsPage      from './pages/RentalsPage';
import IncomingPage     from './pages/IncomingPage';
import LiveMapPage      from './pages/LiveMapPage';
import FleetPage        from './pages/FleetPage';
import MaintenancePage  from './pages/MaintenancePage';
import DamagesPage      from './pages/DamagesPage';
import RevenuePage      from './pages/RevenuePage';
import CustomersPage    from './pages/CustomersPage';
import BookingSetupPage from './pages/BookingSetupPage';
import AgreementsPage   from './pages/AgreementsPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import RentalHistoryPage from './pages/RentalHistoryPage';
import InvoicesPage     from './pages/InvoicesPage';
import RefundsPage      from './pages/RefundsPage';
import UsersRolesPage   from './pages/UsersRolesPage';
import SettingsPage     from './pages/SettingsPage';

// Modals
import AddRentalModal        from './modals/AddRentalModal';
import AddVehicleModal       from './modals/AddVehicleModal';
import AddCustomerModal      from './modals/AddCustomerModal';
import AddMaintenanceModal   from './modals/AddMaintenanceModal';
import EndRentalModal        from './modals/EndRentalModal';
import ExtendRentalModal     from './modals/ExtendRentalModal';
import InvoiceViewModal      from './modals/InvoiceViewModal';
import InvoiceCreateModal    from './modals/InvoiceCreateModal';
import AgreementModal        from './modals/AgreementModal';
import RoleEditorModal       from './modals/RoleEditorModal';
import InviteUserModal       from './modals/InviteUserModal';
import CustomerProfileModal  from './modals/CustomerProfileModal';
import AddPOIModal           from './modals/AddPOIModal';
import SwitchUserModal       from './modals/SwitchUserModal';
import CheckInModal          from './modals/CheckInModal';
import BookingEngineModal    from './modals/BookingEngineModal';
import ManageBookingModal    from './modals/ManageBookingModal';
import CalDetailModal        from './modals/CalDetailModal';
import DamageModal           from './modals/DamageModal';
import AddLeaseModal         from './modals/AddLeaseModal';
import EndLeaseModal         from './modals/EndLeaseModal';
import LeaseInvoicesModal    from './modals/LeaseInvoicesModal';
import PublicPortal          from './portal/PublicPortal';

// Detect customer self-service portal URLs. Supports both:
//   /manage/BK-XXXX           — path style (preferred, what email templates use)
//   /manage                   — find-your-booking form (no ref pre-filled)
//   ?manage=BK-XXXX           — query-string fallback (works without SPA fallback hosting)
function detectPortal() {
  if (typeof window === 'undefined') return null;
  // Query string
  const qs = new URLSearchParams(window.location.search);
  if (qs.has('manage')) return { type: 'portal', bookingRef: qs.get('manage') || '' };
  // Path /manage or /manage/BK-XXXX
  const m = window.location.pathname.match(/^\/manage(?:\/(.+))?\/?$/);
  if (m) return { type: 'portal', bookingRef: m[1] || '' };
  return null;
}

export default function App() {
  const route = detectPortal();
  return (
    <AppProvider>
      {route?.type === 'portal'
        ? <PublicPortal bookingRef={route.bookingRef} />
        : <AuthGate />}
    </AppProvider>
  );
}

const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || '/login';

function AuthGate() {
  const { authToken, loading } = useApp();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', color: 'var(--tx3)', fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🚗</div>
          Loading FleetRent…
        </div>
      </div>
    );
  }

  if (!authToken) {
    // Token not found in sessionStorage or localStorage — redirect to login sub-site
    window.location.href = LOGIN_URL;
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', color: 'var(--tx3)', fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔑</div>
          Redirecting to login…
        </div>
      </div>
    );
  }

  return <AppShell />;
}

const ALL_PAGE_IDS = ['rentals','incoming','map','fleet','maintenance','damages','history','invoices','refunds','revenue','customers','bookingsetup','agreement','emailtemplates','roles','settings'];

function AppShell() {
  const { currentPage, isMobile, sidebarOpen, setSidebarOpen, canView, goto, currentUserId } = useApp();
  const isMapPage = currentPage === 'map';

  // Auto-redirect to first allowed page when user switches or lands on a page they can't view
  React.useEffect(() => {
    if (!canView(currentPage)) {
      const firstAllowed = ALL_PAGE_IDS.find(p => canView(p));
      if (firstAllowed) goto(firstAllowed);
    }
  }, [currentUserId, currentPage, canView, goto]);

  return (
    <div className="app">
      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div className="mobile-backdrop show" onClick={() => setSidebarOpen(false)} />
      )}
      <Sidebar />

      <main className="main">
        <Topbar />

        <div className={`content${isMapPage ? ' map-active' : ''}`}>
          <Page id="rentals"      page={currentPage}><RentalsPage /></Page>
          <Page id="incoming"     page={currentPage}><IncomingPage /></Page>
          <Page id="map"          page={currentPage}><LiveMapPage /></Page>
          <Page id="fleet"        page={currentPage}><FleetPage /></Page>
          <Page id="maintenance"  page={currentPage}><MaintenancePage /></Page>
          <Page id="damages"      page={currentPage}><DamagesPage /></Page>
          <Page id="history"      page={currentPage}><RentalHistoryPage /></Page>
          <Page id="revenue"      page={currentPage}><RevenuePage /></Page>
          <Page id="customers"    page={currentPage}><CustomersPage /></Page>
          <Page id="invoices"     page={currentPage}><InvoicesPage /></Page>
          <Page id="refunds"      page={currentPage}><RefundsPage /></Page>
          <Page id="bookingsetup" page={currentPage}><BookingSetupPage /></Page>
          <Page id="agreement"    page={currentPage}><AgreementsPage /></Page>
          <Page id="emailtemplates" page={currentPage}><EmailTemplatesPage /></Page>
          <Page id="roles"        page={currentPage}><UsersRolesPage /></Page>
          <Page id="settings"     page={currentPage}><SettingsPage /></Page>
        </div>
      </main>

      {/* Detail panel (slides in from right) */}
      <DetailPanel />

      {/* Modals */}
      <AddRentalModal />
      <AddVehicleModal />
      <AddCustomerModal />
      <AddMaintenanceModal />
      <EndRentalModal />
      <ExtendRentalModal />
      <InvoiceViewModal />
      <InvoiceCreateModal />
      <AgreementModal />
      <RoleEditorModal />
      <InviteUserModal />
      <CustomerProfileModal />
      <AddPOIModal />
      <SwitchUserModal />
      <CheckInModal />
      <BookingEngineModal />
      <CalDetailModal />
      <ManageBookingModal />
      <DamageModal />
      <AddLeaseModal />
      <EndLeaseModal />
      <LeaseInvoicesModal />

      {/* Toast */}
      <Toast />
    </div>
  );
}

function Page({ id, page, children }) {
  const isActive = page === id;
  return (
    <div className={`page${isActive ? ' active' : ''}`} style={isActive ? {} : { display: 'none' }}>
      {children}
    </div>
  );
}
